/**
 * «Слить названия в одно» — единый паттерн слияния дублей (AK-564).
 *
 * Механика склейки (#714) осталась прежней — одна транзакция, репойнт
 * operations / норм / строк тех.карт на выжившего, источники в архив. Менялись
 * вход и язык, потому что старый разговаривал с человеком терминами разработки:
 *
 *  · «Эталон (survivor)» → «Остаётся»: эталон в этом справочнике — СИСТЕМНАЯ
 *    операция, и звать тем же словом выжившее название значит путать два разных
 *    предмета в одном окне;
 *  · «Посмотреть план (dry-run)» → счётчики считаются сразу при выборе
 *    выжившего: «dry-run» — слово разработчика, а лишний клик перед необратимым
 *    действием ничего не защищает;
 *  · «удалено конфликтов: 2» → отдельный блок со списком того, что будет
 *    удалено, и явной галкой (решение founder 4 от 10.08). Это была самая
 *    опасная строка старого UI: человек жал «Слить», а система молча удаляла
 *    записи норм, столкнувшиеся по уникальному ключу;
 *  · необратимость — отдельной плашкой перед кнопкой, а не фразой в описании.
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { toast } from '@/lib/toast';
import {
  cotVisibilityLabel,
  operationsCountLabel,
  useMergeCompanyOpTypes,
  type CompanyOpType,
  type OpTypeMergeResult,
} from '@/lib/reference/companyOperationTypes';

/**
 * Ключи счётчиков сервера — это `таблица.колонка`. Человеку показываем
 * существительные, а неизвестный ключ (появится новая точка репойнта) НЕ
 * печатаем латиницей: лучше промолчать, чем показать `season_op_cost_lines`.
 */
const SITE_LABEL: Record<string, string> = {
  'operations.company_op_type_id': 'операций',
  'company_work_norms.company_op_type_id': 'норм выработки',
  'season_op_cost_lines.company_op_type_id': 'строк тех.карт',
};

/** Что именно значит «конфликт» в этой таблице — своими словами. */
const CONFLICT_LABEL: Record<string, string> = {
  'company_work_norms.company_op_type_id':
    'нормы выработки совпали — останется норма того названия, которое остаётся',
  'season_op_cost_lines.company_op_type_id':
    'строки тех.карт совпали по сезону и статье — останется строка того названия, которое остаётся',
};

/** Свести счётчики всех источников в один словарь `ключ → сумма`. */
function totalsBySite(
  result: OpTypeMergeResult | null,
  pick: 'repointed' | 'deleted_conflicts',
): Array<[string, number]> {
  if (!result) return [];
  const acc = new Map<string, number>();
  for (const s of result.per_source) {
    for (const [key, n] of Object.entries(s[pick] ?? {})) {
      if (!n) continue;
      acc.set(key, (acc.get(key) ?? 0) + n);
    }
  }
  return [...acc.entries()].filter(([key]) => key in SITE_LABEL);
}

interface Props {
  /** Все выбранные строки (2+). Если <2 — панель не открывается. */
  selected: CompanyOpType[];
  open: boolean;
  onClose: () => void;
  onMerged?: (result: OpTypeMergeResult) => void;
}

export function OpTypeMergeSheet({ selected, open, onClose, onMerged }: Props) {
  const [targetId, setTargetId] = useState<string | null>(null);
  const [preview, setPreview] = useState<OpTypeMergeResult | null>(null);
  const [conflictsAccepted, setConflictsAccepted] = useState(false);
  const merge = useMergeCompanyOpTypes();

  /**
   * По умолчанию остаётся название с наибольшим числом операций — обычно оно и
   * «правильное» (макет, сост. 5). Раньше по умолчанию бралась просто первая
   * выбранная строка, то есть случайная.
   */
  /** Стабильный ключ набора: массив пересобирается на каждом рефетче списка. */
  const selectedKey = selected
    .map((s) => s.id)
    .sort()
    .join(',');

  useEffect(() => {
    if (!open || selected.length === 0) return;
    const best = [...selected].sort(
      (a, b) => (b.usage_count ?? 0) - (a.usage_count ?? 0),
    )[0];
    setTargetId(best.id);
    setPreview(null);
    setConflictsAccepted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedKey]);

  const target = useMemo(
    () => selected.find((s) => s.id === targetId) ?? null,
    [selected, targetId],
  );
  const sources = useMemo(
    () => selected.filter((s) => s.id !== targetId),
    [selected, targetId],
  );

  /**
   * Ключ пары «выживший + источники». В зависимостях эффекта именно он, а не
   * массив `selected`: массив пересобирается на каждом рефетче списка, и
   * предпросмотр уходил бы в петлю.
   */
  const sourceKey = sources.map((s) => s.id).sort().join(',');

  /**
   * Счётчики считаем сразу, как только известна пара «выживший + источники».
   * `dry_run` на сервере откатывает транзакцию, побочных эффектов нет.
   */
  useEffect(() => {
    if (!open || !target || sources.length === 0) return;
    let cancelled = false;
    setPreview(null);
    setConflictsAccepted(false);
    merge
      .mutateAsync({
        target_id: target.id,
        source_ids: sources.map((s) => s.id),
        dry_run: true,
      })
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch(() => {
        /* показывается через merge.isError ниже */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetId, sourceKey]);

  const repointed = totalsBySite(preview, 'repointed');
  const conflicts = totalsBySite(preview, 'deleted_conflicts');
  const hasConflicts = conflicts.length > 0;
  /** Сливать названия с разными системными операциями — почти всегда ошибка. */
  const mixedRefs =
    target !== null &&
    sources.some((s) => (s.ref_id ?? null) !== (target.ref_id ?? null));

  const busy = merge.isPending;
  const canMerge =
    !busy &&
    target !== null &&
    sources.length > 0 &&
    preview !== null &&
    (!hasConflicts || conflictsAccepted);

  /** «Где будет видно» — объединение областей, чтобы никто не потерял название. */
  const resultScope = useMemo(() => {
    if (!target) return '';
    if (selected.some((s) => s.scope_level === 'company')) return 'Весь холдинг';
    return cotVisibilityLabel(target);
  }, [selected, target]);

  function confirm() {
    if (!target) return;
    merge.mutate(
      {
        target_id: target.id,
        source_ids: sources.map((s) => s.id),
        dry_run: false,
      },
      {
        onSuccess: (result) => {
          toast.success(`Названия слиты в «${target.local_name}»`);
          onMerged?.(result);
          onClose();
        },
        onError: (e) => toast.error(e.message || 'Не удалось слить названия'),
      },
    );
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-4 overflow-y-auto sm:max-w-lg"
      >
        <SheetHeader>
          <SheetTitle>Слить {selected.length} названия в одно</SheetTitle>
          <SheetDescription>
            Отметьте, какое название останется. Все операции, нормы и строки
            тех.карт перепривяжутся на него за одно действие, остальные названия
            уйдут в архив.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label>Остаётся</Label>
            <RadioGroup
              className="flex flex-col gap-2"
              value={targetId ?? ''}
              onValueChange={(v) => setTargetId(v)}
            >
              {selected.map((row) => (
                <label
                  key={row.id}
                  htmlFor={`survivor-${row.id}`}
                  className="flex items-center gap-3 rounded-md border border-border p-3 hover:bg-accent"
                >
                  <RadioGroupItem id={`survivor-${row.id}`} value={row.id} />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium">
                      {row.local_name}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {row.ref_name_ru ?? 'своя запись'} ·{' '}
                      {cotVisibilityLabel(row)} ·{' '}
                      {operationsCountLabel(row.usage_count ?? 0)}
                    </span>
                  </div>
                </label>
              ))}
            </RadioGroup>
            <p className="text-xs text-muted-foreground">
              По умолчанию отмечено название с наибольшим числом операций —
              обычно оно и «правильное».
            </p>
          </div>

          {mixedRefs && (
            <Alert>
              <AlertTitle>Это разные работы</AlertTitle>
              <AlertDescription>
                У выбранных названий разные системные операции. Обычно так
                выглядят не дубли, а две разные работы — проверьте прежде, чем
                сливать.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col gap-2 rounded-lg border border-border p-4">
            <p className="text-sm font-medium text-foreground">Что произойдёт</p>
            {busy && !preview ? (
              <p className="text-sm text-muted-foreground">Считаем…</p>
            ) : (
              <>
                <ul className="flex flex-col gap-1 text-sm text-muted-foreground">
                  {repointed.length === 0 ? (
                    <li>Переносить нечего — по схлопываемым названиям пусто.</li>
                  ) : (
                    repointed.map(([key, n]) => (
                      <li key={key}>
                        Перепривяжется {SITE_LABEL[key]}: {n}
                      </li>
                    ))
                  )}
                  <li>Уйдёт в архив названий: {preview?.archived.length ?? sources.length}</li>
                </ul>
                <p className="text-sm text-muted-foreground">
                  Где будет видно: {resultScope}
                </p>
              </>
            )}
          </div>

          {/* Явное подтверждение удаления — решение founder 4 (10.08) */}
          {hasConflicts && (
            <Alert variant="destructive">
              <AlertTitle>Часть записей будет удалена</AlertTitle>
              <AlertDescription className="flex flex-col gap-2">
                <ul className="flex list-disc flex-col gap-1 pl-4">
                  {conflicts.map(([key, n]) => (
                    <li key={key}>
                      {n} {SITE_LABEL[key]}: {CONFLICT_LABEL[key] ?? 'останется запись выжившего названия'}
                    </li>
                  ))}
                </ul>
                <label className="flex items-start gap-2 text-sm">
                  <Checkbox
                    checked={conflictsAccepted}
                    onCheckedChange={(v) => setConflictsAccepted(v === true)}
                    aria-label="Согласен, что перечисленные записи будут удалены"
                  />
                  <span>Понимаю: перечисленное будет удалено без возврата.</span>
                </label>
              </AlertDescription>
            </Alert>
          )}

          <Alert variant="warning">
            <AlertTitle>Отменить слияние нельзя</AlertTitle>
            <AlertDescription>
              Схлопнутые названия останутся в архиве, но операции уже будут
              перепривязаны на то, которое остаётся.
            </AlertDescription>
          </Alert>

          {merge.isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{merge.error.message}</AlertDescription>
            </Alert>
          )}
        </div>

        <SheetFooter className="mt-auto flex-col gap-2 sm:flex-row">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button onClick={confirm} disabled={!canMerge}>
            {busy && !merge.variables?.dry_run && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Слить
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default OpTypeMergeSheet;
